/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  Flag,
  Plus,
  Search,
  CheckCircle2,
  Clock,
  Trash2,
  Edit2,
  Calendar,
  AlertTriangle,
  X,
} from "lucide-react";

export default function Banners() {
  const [bannerList, setBannerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingBannerId, setDeletingBannerId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBannerId, setSelectedBannerId] = useState(null);
  const [selectedBannerText, setSelectedBannerText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "published", "inactive"

  const { listOfAllBanners, deleteBanner } = useContext(APIContext);

  // Helper function to check if banner is effectively published
  const isBannerPublished = (banner) => {
    if (!banner.published) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000); // Current time in seconds

    if (banner.start_date) {
      const startDate =
        typeof banner.start_date === "number"
          ? banner.start_date
          : parseInt(banner.start_date);
      if (startDate > now) {
        return false;
      }
    }

    if (banner.expiry_date) {
      const expiryDate =
        typeof banner.expiry_date === "number"
          ? banner.expiry_date
          : parseInt(banner.expiry_date);
      if (expiryDate < now) {
        return false;
      }
    }

    return true;
  };

  const fetchBanners = () => {
    listOfAllBanners()
      .then((banners) => {
        setBannerList(Array.isArray(banners) ? banners : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load banners");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleDeleteClick = (bannerId, bannerText) => {
    setSelectedBannerId(bannerId);
    setSelectedBannerText(bannerText || "Untitled Banner");
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!selectedBannerId || deletingBannerId) return;

    setDeletingBannerId(selectedBannerId);
    deleteBanner(selectedBannerId)
      .then(() => {
        setDeletingBannerId(null);
        toast.success("Banner deleted successfully!");
        setIsDeleteModalOpen(false);
        fetchBanners();
      })
      .catch((error) => {
        console.error(error);
        setDeletingBannerId(null);
        toast.error("Failed to delete banner");
      });
  };

  const publishedCount = useMemo(() => {
    return bannerList.filter((b) => isBannerPublished(b)).length;
  }, [bannerList]);

  const inactiveCount = useMemo(() => {
    return bannerList.length - publishedCount;
  }, [bannerList, publishedCount]);

  const filteredBanners = useMemo(() => {
    return bannerList.filter((banner) => {
      const name = (banner.banner_name || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || name.includes(q);

      const isPub = isBannerPublished(banner);
      let matchesStatus = true;
      if (statusFilter === "published") matchesStatus = isPub;
      if (statusFilter === "inactive") matchesStatus = !isPub;

      return matchesSearch && matchesStatus;
    });
  }, [bannerList, searchQuery, statusFilter]);

  const formatDate = (timestamp) => {
    if (!timestamp) return null;
    const sec = typeof timestamp === "number" ? timestamp : parseInt(timestamp);
    if (isNaN(sec) || sec <= 0) return null;
    return new Date(sec * 1000).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <>
      <HelmetLayout title="Banners" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Banners</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Create and manage system announcements, top notification bars, and promotional banners.
            </p>
          </div>
          <Link
            to="/banners/add"
            className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20 flex items-center gap-2"
          >
            <Plus size={16} />
            Add New Banner
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Banners</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : bannerList.length}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <Flag size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Active / Published</p>
              <h3 className="text-2xl font-bold text-emerald-600">
                {loading ? "—" : publishedCount}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Inactive / Draft</p>
              <h3 className="text-2xl font-bold text-slate-600">
                {loading ? "—" : inactiveCount}
              </h3>
            </div>
            <div className="p-3 bg-slate-100 text-slate-600 rounded-xl">
              <Clock size={22} />
            </div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
          <div className="relative flex-1 max-w-md">
            <Search
              size={18}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search banners by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#623ce7] focus:ring-1 focus:ring-[#623ce7] transition-colors"
            />
          </div>

          <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-lg w-fit self-start md:self-auto">
            <button
              onClick={() => setStatusFilter("all")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "all"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              All ({bannerList.length})
            </button>
            <button
              onClick={() => setStatusFilter("published")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "published"
                  ? "bg-white text-emerald-700 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Published ({publishedCount})
            </button>
            <button
              onClick={() => setStatusFilter("inactive")}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === "inactive"
                  ? "bg-white text-slate-800 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Inactive ({inactiveCount})
            </button>
          </div>
        </div>

        {/* Banners List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
            <PulseLoader size={12} color="#623ce7" />
            <p className="text-slate-400 text-sm mt-4">Loading banners...</p>
          </div>
        ) : filteredBanners.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-sm">
            <Flag size={36} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-base font-semibold text-slate-700 mb-1">No banners found</h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your search query or filter selection."
                : "No promotional banners have been created yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3.5">
            {filteredBanners.map((banner) => {
              const bannerID =
                banner.banner_id ||
                (banner.pk ? banner.pk.replace("banner-", "") : null);
              if (!bannerID) return null;

              const isPublished = isBannerPublished(banner);
              const startDateStr = formatDate(banner.start_date);
              const expiryDateStr = formatDate(banner.expiry_date);
              const bannerBg = banner.banner_color || "#623ce7";

              return (
                <div
                  key={bannerID}
                  className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  <div className="flex items-start gap-3.5 flex-1 min-w-0">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0 shadow-sm"
                      style={{ backgroundColor: bannerBg }}
                      title={`Color: ${bannerBg}`}
                    >
                      <Flag size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <Link
                          to={`/banners/${bannerID}`}
                          className="text-base font-semibold text-slate-900 hover:text-[#623ce7] transition-colors truncate"
                        >
                          {banner.banner_name || "Untitled Banner"}
                        </Link>
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            isPublished
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-slate-100 text-slate-600 border border-slate-200"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isPublished ? "bg-emerald-500" : "bg-slate-400"
                            }`}
                          />
                          {isPublished ? "Published" : "Draft / Inactive"}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                        {startDateStr || expiryDateStr ? (
                          <div className="flex items-center gap-1.5">
                            <Calendar size={13} className="text-slate-400" />
                            <span>
                              {startDateStr || "Immediate"} — {expiryDateStr || "No expiry"}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400">Always active</span>
                        )}
                        <span className="text-slate-300">•</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-slate-400">Color:</span>
                          <span
                            className="inline-block w-3 h-3 rounded-full border border-slate-300"
                            style={{ backgroundColor: bannerBg }}
                          />
                          <span className="font-mono text-slate-600">{bannerBg}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0 self-end md:self-center">
                    <Link
                      to={`/banners/${bannerID}`}
                      className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <Edit2 size={13} />
                      Edit Banner
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteClick(bannerID, banner.banner_name)
                      }
                      className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Delete banner"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 transform transition-all">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">Delete Banner</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Are you sure you want to delete{" "}
                  <strong className="text-slate-800 font-semibold">
                    "{selectedBannerText}"
                  </strong>
                  ? This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={deletingBannerId !== null}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deletingBannerId !== null}
                className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm"
              >
                {deletingBannerId ? (
                  <PulseLoader size={6} color="#ffffff" />
                ) : (
                  "Delete Banner"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
